import {
  BaseEndpointBuilder,
  endpointBuilder,
} from '@squareup/dex-data-shared-base-api';
import {
  ApplePayCertificate,
  CertificateSigningRequest,
} from '@squareup/dex-types-shared-apple-pay-certificates';
import { HTTP_METHOD } from '@squareup/dex-types-shared-http';

import { DEVELOPER_API_REDUCER_PATH } from './developer-api-constants';
import { DeveloperApiTagTypes } from './developer-api-tag-types';
import {
  OnlyApplicationProps,
  WithApplicationProps,
  sandboxModeHeaders,
} from './developer-api-utils';

const DIGITAL_WALLETS_API_URL = '/digital-wallets';

const digitalWalletsEndpointFactory = (
  builder: BaseEndpointBuilder<typeof DEVELOPER_API_REDUCER_PATH>
) => ({
  /**
   * Retrieves a list of Apple Pay certificates for a specific application.
   */
  listApplePayCertificates: builder.query<
    { certificates: ApplePayCertificate[] },
    OnlyApplicationProps
  >({
    query: ({ applicationId, applicationType }) => ({
      path: `${DIGITAL_WALLETS_API_URL}/apple-pay/applications/${applicationId}/certificates`,
      method: HTTP_METHOD.GET,
      additionalHeaders: sandboxModeHeaders(applicationType),
    }),
    providesTags: [DeveloperApiTagTypes.ApplePayCertificate],
  }),
  /**
   * Deletes an Apple Pay certificate for a specific application.
   */
  deleteApplePayCertificate: builder.mutation<
    void,
    WithApplicationProps<{ certificateId: string }>
  >({
    query: ({ applicationId, applicationType, certificateId }) => ({
      path: `${DIGITAL_WALLETS_API_URL}/apple-pay/applications/${applicationId}/certificates/${certificateId}`,
      method: HTTP_METHOD.DELETE,
      additionalHeaders: {
        'Content-Type': 'application/json',
        ...sandboxModeHeaders(applicationType),
      },
    }),
    invalidatesTags: [DeveloperApiTagTypes.ApplePayCertificate],
  }),
  /**
   * Creates a certificate signing request (CSR) file for Apple Pay.
   */
  createCSR: builder.mutation<
    {
      certificate_signing_request: CertificateSigningRequest;
    },
    WithApplicationProps<{ idempotencyKey: string }>
  >({
    query: ({ applicationId, applicationType, idempotencyKey }) => ({
      path: `${DIGITAL_WALLETS_API_URL}/apple-pay/applications/${applicationId}/certificate-signing-requests`,
      method: HTTP_METHOD.POST,
      additionalHeaders: {
        'Content-Type': 'application/json',
        ...sandboxModeHeaders(applicationType),
      },
      body: JSON.stringify({ idempotency_key: idempotencyKey }),
    }),
  }),
  /**
   * Uploads an Apple Pay certificate for a specific application.
   */
  uploadApplePayCertificate: builder.mutation<
    unknown,
    WithApplicationProps<{ certificateId: string; certificate: string }>
  >({
    query: ({
      applicationId,
      applicationType,
      certificateId,
      certificate,
    }) => ({
      path: `/${DIGITAL_WALLETS_API_URL}/apple-pay/applications/${applicationId}/certificates/${certificateId}`,
      method: HTTP_METHOD.PUT,
      additionalHeaders: {
        'Content-Type': 'application/json',
        ...sandboxModeHeaders(applicationType),
      },
      body: JSON.stringify({ certificate }),
    }),
    invalidatesTags: [DeveloperApiTagTypes.ApplePayCertificate],
  }),
});

const digitalWalletsEndpoints = digitalWalletsEndpointFactory(endpointBuilder);

export { digitalWalletsEndpoints, digitalWalletsEndpointFactory };
